import { asPlatforms } from "../../shared/platforms";
import type { Booking, Campaign, Pitch, Talent, User } from "../../shared/types";
import { getCurrentMonthDue } from "../../utils/dates";

/**
 * Same booking fields as New Booking, plus pitchId. The approve write assigns the booking id.
 * Do not import New Booking page state.
 */
export function bookingFromApprovedPitch(args: {
    campaign: Campaign;
    pitch: Pitch;
    talent?: Talent;
    booker?: User;
    approver?: User;
}): Omit<Booking, "id"> {
    const { campaign, pitch, talent, booker, approver } = args;
    const bookerId = booker?.id || campaign.booker?.uid;
    const bookerEmail = booker?.email || campaign.booker?.email;
    const airtableBookerId = booker?.airtableBookerId || campaign.booker?.airtableBookerId;
    const clientName = booker?.company || campaign.booker?.company;
    const talentName =
        talent?.platformData?.TikTok?.username ||
        talent?.platformData?.Instagram?.username ||
        pitch.talentName;
    const salesTeamId = campaign.salesTeamId || [];
    const isTest = Boolean(
        campaign.isTest || bookerEmail === "timothy.noetzel@gmail.com" || import.meta.env.DEV,
    );
    const placedByRoles = Object.entries(approver?.roles || {})
        .filter(([, value]) => Boolean(value))
        .map(([key]) => key as keyof User["roles"]);

    return {
        airtableId: null,
        airtableCampaignId: null,
        airtableClientId: booker?.airtableClientId || "",
        talentId: pitch.talentId,
        price: pitch.price,
        ...(talentName ? { talentName } : {}),
        managerIds: talent?.managerIds || [],
        ...(talent?.airtableManagerIds?.length ? { airtableManagerIds: talent.airtableManagerIds } : {}),
        ...(bookerId ? { bookerId } : {}),
        ...(airtableBookerId ? { airtableBookerId } : {}),
        campaignId: campaign.id || pitch.campaignId,
        ...(campaign.campaignName ? { campaignName: campaign.campaignName } : {}),
        ...(campaign.campaignType ? { campaignType: campaign.campaignType } : {}),
        campaignPlatform: asPlatforms(campaign.campaignPlatform),
        ...(booker?.clientId ? { clientId: booker.clientId } : {}),
        ...(clientName ? { clientName } : {}),
        ...(campaign.crmLeadId ? { crmLeadId: campaign.crmLeadId } : {}),
        leadSource: campaign.leadSource || [],
        ...(approver?.email ? { placedByEmail: approver.email } : {}),
        ...(approver?.id ? { placedById: approver.id } : {}),
        placedByRoles,
        placedByCreatorsManager: Boolean(approver?.id && talent?.managerIds?.includes(approver.id)),
        createdByManager: Boolean(approver?.roles?.manager),
        lastUpdateByManager: Boolean(approver?.roles?.manager),
        ...(campaign.currency ? { currency: campaign.currency } : {}),
        ...(isTest ? { isTest: true } : {}),
        monthDue2: getCurrentMonthDue(),
        ...(salesTeamId.length > 0
            ? {
                salesLeadId: salesTeamId,
                airtableSalesLeadId: campaign.airtableSalesTeamId || [],
            }
            : {}),
        pitchId: pitch.id,
    };
}
